import React from 'react';
import Layout from '../components/global/layout';
import SEO from '../components/seo';
import PageBanner from '../components/layouts/page-banner';
import { Container } from '@material-ui/core';
import LightMode from '../components/global/light-mode';
import VerticalPadding from '../components/global/vertical-padding';

const TermsOfServiceAnnual: React.FC = () => {
  return (
    <Layout>
      <SEO
        title="Terms of Service (Annual)"
        description="Annual Subscription Terms of Service governing Marvel &amp; Snap annual subscription services."
      />
      <PageBanner heading="Terms of Service (Annual)" />
      <LightMode>
        <VerticalPadding>
          <Container maxWidth="lg" style={{ lineHeight: 1.77 }}>
            <p>
              <small>Last updated on July 1, 2026</small>
            </p>
            <p>
              These Annual Subscription Terms of Service govern the annual
              subscription services provided by Marvel &amp; Snap Inc. (“Marvel
              &amp; Snap”) to the customer identified in the applicable order,
              proposal, statement of work, or subscription agreement (the
              “Customer”).
            </p>
            <p>
              By signing or approving an order, requesting services, paying an
              invoice, or otherwise using the Services, the Customer agrees to
              be bound by these Terms.
            </p>
            <p>
              These Terms apply only to annual subscriptions. Existing monthly
              subscriptions remain governed by the terms in effect for those
              subscriptions until the Customer is migrated to an annual
              subscription or otherwise agrees in writing to these Terms.
            </p>
            <h2>1. Definitions</h2>
            <p>In these Terms:</p>
            <p>
              “Agreement” means these Terms, together with any applicable order,
              proposal, statement of work, subscription agreement, renewal
              confirmation, or written amendment accepted by both parties.
            </p>
            <p>
              “Business Day” means Monday to Friday, excluding statutory
              holidays in Ontario.
            </p>
            <p>
              “Customer Content” means any text, images, media, data, documents,
              credentials, access permissions, brand assets, instructions,
              materials, or other information provided by or on behalf of the
              Customer.
            </p>
            <p>
              “Deliverables” means the work product created by Marvel &amp; Snap
              specifically for the Customer as part of the Services, excluding
              Marvel &amp; Snap Materials and Third-Party Materials.
            </p>
            <p>
              “Fees” means the subscription fees, add-on fees, project fees,
              third-party fees, taxes, and any other amounts payable by the
              Customer under the Agreement.
            </p>
            <p>
              “Marvel &amp; Snap Materials” means Marvel &amp; Snap’s
              pre-existing or independently developed tools, templates,
              processes, systems, methods, documentation, know-how, frameworks,
              code libraries, reusable components, internal workflows, and other
              proprietary materials.
            </p>
            <p>
              “Services” means the ongoing web operations, design, development,
              hosting, technical support, technical SEO, maintenance,
              consulting, and related services described in the applicable order
              or otherwise agreed to in writing.
            </p>
            <p>
              “Third-Party Materials” means products, services, platforms,
              plugins, themes, software, APIs, fonts, stock assets, hosting
              services, payment processors, analytics tools, AI-enabled tools,
              or other materials owned or controlled by third parties.
            </p>
            <h2>2. Scope of Services</h2>
            <p>
              Marvel &amp; Snap will provide the Services described in the
              applicable order or otherwise agreed to in writing.
            </p>
            <p>
              The Services are intended to support the Customer’s website
              operations and may include design, development, maintenance,
              hosting, technical support, technical SEO, performance
              improvements, accessibility support, troubleshooting, quality
              assurance, and related website services. Unless expressly stated
              in writing, the Services do not include digital marketing, paid
              advertising, marketing strategy, copywriting, SEO keyword
              research, ongoing SEO campaign management, legal advice,
              regulatory compliance advice, or services outside Marvel &amp;
              Snap’s normal web operations scope.
            </p>
            <p>
              Marvel &amp; Snap may decline, defer, or require a separate
              agreement for work that falls outside the agreed scope, requires
              specialized third-party services, creates unreasonable operational
              risk, or cannot reasonably be completed within Marvel &amp; Snap’s
              available capacity.
            </p>
            <h2>3. Annual Subscription Term</h2>
            <p>
              Each subscription has an initial term of twelve (12) months
              beginning on the start date set out in the applicable order or, if
              no start date is specified, on the date the order is accepted by
              Marvel &amp; Snap.
            </p>
            <p>
              The subscription is billed monthly in advance during the annual
              term. At the end of the initial term, the subscription will
              automatically renew for successive twelve (12) month renewal terms
              unless either party provides written notice of non-renewal at
              least thirty (30) days before the end of the then-current term.
            </p>
            <p>
              Upon renewal, Marvel &amp; Snap’s then-current fees, service
              structure, and terms may apply unless otherwise agreed in writing.
            </p>
            <h2>4. Fees, Invoicing, and Payment</h2>
            <p>
              Marvel &amp; Snap will invoice the Customer monthly in advance
              unless otherwise stated in the applicable order.
            </p>
            <p>
              Invoices are due within thirty (30) days of the invoice date
              unless a different payment period is stated in writing.
            </p>
            <p>
              All Fees are exclusive of applicable taxes, unless expressly
              stated otherwise.
            </p>
            <p>
              The Customer is responsible for all applicable taxes, duties,
              levies, and similar charges.
            </p>
            <p>
              All payments are non-refundable, except where required by law or
              expressly agreed to in writing by Marvel &amp; Snap.
            </p>
            <p>
              The Customer may not withhold, set off, or reduce payment because
              of a dispute unless required by law.
            </p>
            <p>
              Any billing concerns must be raised promptly and in good faith.
            </p>
            <p>
              Any overdue amounts shall bear interest at a rate of 1% per month
              (or the maximum rate permitted by law, if less), calculated from
              the original due date until the date of payment, and the Customer
              shall reimburse Marvel &amp; Snap for all costs of collection,
              including reasonable legal fees.
            </p>
            <h2>5. Payment Default and Suspension</h2>
            <p>
              If an invoice is overdue, Marvel &amp; Snap may provide written
              notice requiring payment.
            </p>
            <p>
              If payment remains outstanding after notice, Marvel &amp; Snap may
              suspend some or all Services until the overdue amounts are paid.
            </p>
            <p>
              Suspension of Services does not relieve the Customer of its
              payment obligations.
            </p>
            <p>
              If the Customer’s payment default continues for more than fifteen
              (15) days after written notice, Marvel &amp; Snap may, without
              limiting any other rights or remedies:
            </p>
            <ul>
              <li>suspend Services;</li>
              <li>require payment before resuming Services;</li>
              <li>
                declare all remaining Fees for the current term immediately due
                and payable;
              </li>
              <li>terminate the Agreement; and/or</li>
              <li>
                recover reasonable collection costs, legal fees, and expenses
                incurred in collecting overdue amounts.
              </li>
            </ul>
            <p>
              Marvel &amp; Snap is not responsible for delays, service
              interruptions, missed deadlines, website issues, or other
              consequences resulting from suspension due to non-payment.
            </p>
            <h2>6. Service Delivery and Scheduling</h2>
            <p>
              All work will be managed through Marvel &amp; Snap’s established
              service delivery process.
            </p>
            <p>
              Marvel &amp; Snap’s standard business hours are 9:00 a.m. to 5:00
              p.m. Eastern Time, Monday to Friday, excluding statutory holidays
              in Ontario.
            </p>
            <p>
              Requests submitted outside standard business hours may be received
              or logged, but Marvel &amp; Snap does not guarantee review,
              response, commencement, or resolution until the next Business Day
              unless a separate after-hours support arrangement has been agreed
              to in writing.
            </p>
            <p>
              Each request will be reviewed, clarified where needed,
              prioritized, scoped, and scheduled based on its urgency,
              complexity, dependencies, business impact, and Marvel &amp; Snap’s
              operational capacity.
            </p>
            <p>
              The subscription removes fixed hourly limits for in-scope work,
              but it does not remove practical capacity constraints.
            </p>
            <p>
              The Customer acknowledges that an annual subscription does not
              require Marvel &amp; Snap to provide unlimited labour, guarantee
              immediate commencement of every request, or guarantee completion
              of all requests within a specific timeframe.
            </p>
            <p>
              Marvel &amp; Snap may work on one or more requests at a time based
              on priority, available capacity, required expertise, and
              sequencing. Some requests may need to wait while higher-priority,
              more urgent, or already-scheduled work is completed.
            </p>
            <p>
              Marvel &amp; Snap may provide estimated timelines, target dates,
              or expected delivery windows.
            </p>
            <p>
              These are planning tools only and are not binding deadlines unless
              expressly agreed to in writing by Marvel &amp; Snap.
            </p>
            <p>
              Where a specific deadline is required, the parties will work
              together to determine whether the requested timeline is
              reasonable.
            </p>
            <p>
              Marvel &amp; Snap may require the Customer to prioritize work,
              reduce scope, approve additional fees, or accept a later delivery
              date.
            </p>
            <h2>7. Customer Responsibilities</h2>
            <p>
              The Customer is responsible for providing timely access,
              approvals, feedback, information, credentials, content, and
              instructions reasonably required for Marvel &amp; Snap to perform
              the Services.
            </p>
            <p>
              The Customer is responsible for the accuracy, legality,
              completeness, and appropriateness of all Customer Content.
            </p>
            <p>
              The Customer must ensure that it has the necessary rights,
              licences, permissions, and consents to provide Customer Content to
              Marvel &amp; Snap and to use it in connection with the Services.
            </p>
            <p>
              The Customer is responsible for reviewing and approving
              Deliverables, including text, images, layouts, functionality,
              legal content, privacy content, regulatory content, and
              business-specific information.
            </p>
            <p>
              Marvel &amp; Snap is not responsible for delays, defects, missed
              timelines, or additional costs caused by incomplete information,
              delayed feedback, lack of access, third-party issues, changes in
              Customer priorities, or Customer decisions.
            </p>
            <h2>8. Change Requests and Out-of-Scope Work</h2>
            <p>
              Marvel &amp; Snap may determine that a request is outside the
              scope of the subscription if it requires specialized services,
              unusually high effort, third-party procurement, significant new
              functionality, major redesign work, emergency turnaround, services
              outside Marvel &amp; Snap’s expertise, or work not reasonably
              contemplated by the applicable order.
            </p>
            <p>
              Out-of-scope work may require a separate estimate, add-on fee,
              project agreement, or written approval before work begins.
            </p>
            <p>
              Marvel &amp; Snap is not required to begin out-of-scope work
              unless the parties have agreed to the scope, fees, timing, and any
              applicable terms.
            </p>
            <h2>9. AI-Assisted Services</h2>
            <p>
              Marvel &amp; Snap uses AI-enabled tools as part of its modern
              service delivery workflow.
            </p>
            <p>
              These tools may support development, code assistance, design
              workflows, quality assurance, accessibility testing, security
              review, research, planning, internal drafting, troubleshooting,
              documentation, and other productivity-related tasks.
            </p>
            <p>
              The Customer acknowledges and agrees that AI-assisted workflows
              may be used in connection with the Services.
            </p>
            <p>
              The Customer may not require Marvel &amp; Snap to opt out of,
              disable, or restrict AI usage as a condition of receiving the
              Services, provided Marvel &amp; Snap follows the safeguards
              described in this section.
            </p>
            <p>
              Marvel &amp; Snap remains responsible for the quality, security,
              and professional review of the work it delivers.
            </p>
            <p>
              AI tools are used to assist Marvel &amp; Snap’s team, not to
              replace human judgment, professional oversight, or quality
              assurance.
            </p>
            <p>
              Marvel &amp; Snap will not knowingly use AI tools that train
              public models on Customer Confidential Information.
            </p>
            <p>
              Marvel &amp; Snap will also take reasonable steps to avoid
              intentionally submitting Sensitive Data to AI tools unless
              appropriate approval has been obtained and the use is reasonably
              required for the Services.
            </p>
            <p>
              For the purpose of this section, “Sensitive Data” includes
              confidential customer information, personal information, regulated
              data, credentials, API keys, tokens, passwords, private business
              information, proprietary technical information, private source
              code, unpublished designs, and other non-public materials that
              reasonably require heightened protection.
            </p>
            <p>
              The Customer is responsible for not sending Marvel &amp; Snap
              unnecessary Sensitive Data and for identifying any information
              that requires special handling.
            </p>
            <p>
              Where AI-assisted work is used, Marvel &amp; Snap will use
              reasonable human oversight before deploying or delivering material
              outputs, including AI-assisted code, design assets, technical
              recommendations, documentation, or other work product.
            </p>
            <p>
              The Customer acknowledges that many modern software, design,
              development, hosting, testing, security, and productivity
              platforms include AI-enabled or automated features.
            </p>
            <p>
              Marvel &amp; Snap may use such tools where it reasonably considers
              them appropriate for the Services, subject to the safeguards in
              this Agreement.
            </p>
            <h2>10. Ownership of Deliverables</h2>
            <p>
              Subject to full payment of all related invoices, the Customer will
              own the final Deliverables created specifically for the Customer
              under the Agreement. Ownership does not transfer until all
              invoices related to the applicable Deliverables, Services,
              subscription term, third-party costs, and any related work have
              been paid in full. Until payment is made in full, Marvel &amp;
              Snap retains ownership of the Deliverables and may withhold files,
              transfer of rights, launch approvals, credentials, documentation,
              or other materials related to the unpaid work.
            </p>
            <p>
              Marvel &amp; Snap retains all rights, title, and interest in
              Marvel &amp; Snap Materials, including any Marvel &amp; Snap
              Materials that are incorporated into or used to create the
              Deliverables. To the extent Marvel &amp; Snap Materials are
              included in a paid Deliverable, Marvel &amp; Snap grants the
              Customer a non-exclusive, perpetual, worldwide licence to use
              those Marvel &amp; Snap Materials solely as part of the
              Deliverable and solely for the Customer’s normal business
              purposes.
            </p>
            <p>
              The Customer may not extract, resell, redistribute, sublicense,
              reverse engineer, commercialize, or separately use Marvel &amp;
              Snap Materials except as expressly permitted in writing.
            </p>
            <h2>11. Third-Party Materials</h2>
            <p>
              The Services may involve Third-Party Materials. Third-Party
              Materials are governed by their own terms, licences, privacy
              policies, service levels, warranties, and fees.
            </p>
            <p>
              Marvel &amp; Snap is not responsible for third-party changes,
              outages, pricing increases, service limitations, security
              incidents, discontinued products, licensing disputes, or failures.
            </p>
            <p>
              The Customer is responsible for all third-party fees, licences,
              subscriptions, renewals, approvals, and account obligations unless
              otherwise agreed in writing.
            </p>
            <p>
              Marvel &amp; Snap may recommend, configure, integrate, or support
              Third-Party Materials, but does not guarantee their ongoing
              availability, performance, security, compatibility, or
              suitability.
            </p>
            <h2>12. Customer Content and Licence to Marvel &amp; Snap</h2>
            <p>The Customer retains ownership of Customer Content.</p>
            <p>
              The Customer grants Marvel &amp; Snap a non-exclusive,
              royalty-free licence to access, use, reproduce, modify, display,
              publish, host, transmit, and create derivative works from Customer
              Content solely as necessary to provide the Services.
            </p>
            <p>
              The Customer represents that Customer Content does not infringe
              any third-party rights, violate applicable law, or contain
              unlawful, harmful, defamatory, misleading, or unauthorized
              material.
            </p>
            <p>
              Marvel &amp; Snap may remove, disable, or decline to publish
              Customer Content that Marvel &amp; Snap reasonably believes may
              create legal, security, reputational, technical, or operational
              risk.
            </p>
            <h2>13. Confidentiality</h2>
            <p>
              Each party may receive confidential or proprietary information
              from the other party in connection with the Agreement.
            </p>
            <p>
              Each party agrees to protect the other party’s confidential
              information using reasonable care and to use it only for purposes
              of performing or receiving the Services. Confidential information
              does not include information that is publicly available, already
              known without restriction, independently developed without use of
              the other party’s confidential information, or lawfully received
              from a third party.
            </p>
            <p>
              Marvel &amp; Snap may disclose confidential information to its
              employees, contractors, advisors, service providers, and platform
              vendors as reasonably necessary to provide the Services, provided
              such disclosure is subject to appropriate confidentiality
              obligations or professional duties.
            </p>
            <p>
              Marvel &amp; Snap may also disclose confidential information where
              required by law, court order, regulator, or legal process,
              provided Marvel &amp; Snap gives the Customer notice where legally
              permitted.
            </p>
            <h2>14. Security and Access</h2>
            <p>
              Marvel &amp; Snap will use commercially reasonable efforts to
              protect systems, credentials, and Customer information accessed in
              connection with the Services.
            </p>
            <p>
              The Customer is responsible for maintaining appropriate access
              controls, user permissions, internal approvals, password
              practices, and security policies for its own accounts and systems.
            </p>
            <p>
              The Customer must promptly notify Marvel &amp; Snap of any
              suspected unauthorized access, compromised credential, security
              incident, or change in personnel affecting account access.
            </p>
            <p>
              Marvel &amp; Snap is not responsible for security incidents caused
              by the Customer, the Customer’s users, third-party platforms, weak
              passwords, shared credentials, unauthorized plugins, unsupported
              software, or systems outside Marvel &amp; Snap’s reasonable
              control.
            </p>
            <h2>15. Hosting and Website Availability</h2>
            <p>
              Where hosting is included in the Services, Marvel &amp; Snap will
              provide or manage hosting through third-party hosting providers.
            </p>
            <p>
              Marvel &amp; Snap will use commercially reasonable efforts to
              maintain website availability and respond to hosting-related
              incidents. However, Marvel &amp; Snap does not guarantee
              uninterrupted, error-free, or loss-free hosting. Hosting
              performance may depend on third-party providers, internet
              infrastructure, DNS services, plugins, themes, content delivery
              networks, customer-side changes, traffic spikes, cyberattacks, and
              other factors outside Marvel &amp; Snap’s control.
            </p>
            <h2>16. Warranty Disclaimer</h2>
            <p>
              Marvel &amp; Snap warrants that it will perform the Services in a
              professional and commercially reasonable manner. Except as
              expressly stated in this Agreement, the Services and Deliverables
              are provided “as is” and “as available.” To the maximum extent
              permitted by law, Marvel &amp; Snap disclaims all warranties,
              representations, and conditions, whether express, implied,
              statutory, or otherwise, including warranties of merchantability,
              fitness for a particular purpose, non-infringement, uninterrupted
              operation, error-free performance, search ranking outcomes,
              revenue results, conversion results, accessibility compliance,
              regulatory compliance, or compatibility with all third-party
              systems.
            </p>
            <h2>17. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, neither party will be
              liable for indirect, incidental, special, consequential,
              aggravated, exemplary, or punitive damages, including lost
              profits, lost revenue, loss of business, loss of goodwill, loss of
              data, business interruption, or the cost of substitute services.
              Except for payment obligations, confidentiality obligations,
              infringement of Marvel &amp; Snap Materials, misuse of
              intellectual property, or a party’s gross negligence, fraud, or
              wilful misconduct, Marvel &amp; Snap’s total aggregate liability
              arising out of or relating to the Agreement will not exceed the
              Fees paid by the Customer to Marvel &amp; Snap in the three (3)
              months immediately preceding the event giving rise to the claim.
            </p>
            <p>
              The limitations in this section apply regardless of the form of
              claim, whether in contract, tort, negligence, strict liability,
              statute, or otherwise, even if a party has been advised of the
              possibility of such damages.
            </p>
            <h2>18. Indemnity</h2>
            <p>
              The Customer will defend, indemnify, and hold harmless Marvel
              &amp; Snap and its directors, officers, employees, contractors,
              and representatives from and against any third-party claims,
              damages, losses, liabilities, costs, and expenses, including
              reasonable legal fees, arising from:
            </p>
            <ul>
              <li>Customer Content;</li>
              <li>the Customer’s breach of the Agreement;</li>
              <li>the Customer’s violation of applicable law;</li>
              <li>the Customer’s misuse of the Services or Deliverables;</li>
              <li>instructions provided by the Customer;</li>
              <li>
                third-party materials selected, supplied, or required by the
                Customer; or
              </li>
              <li>
                the Customer’s failure to obtain required rights, licences,
                consents, or approvals.
              </li>
            </ul>
            <p>
              Marvel &amp; Snap will defend, indemnify, and hold harmless the
              Customer from third-party claims alleging that the final
              Deliverables, as provided by Marvel &amp; Snap and used as
              permitted under this Agreement, infringe a Canadian copyright or
              trademark, provided that the claim does not arise from Customer
              Content, Third-Party Materials, Customer instructions,
              modifications not made by Marvel &amp; Snap, or use outside the
              scope of the Agreement.
            </p>
            <p>
              The indemnified party must promptly notify the indemnifying party
              of the claim, provide reasonable cooperation, and allow the
              indemnifying party to control the defence and settlement, provided
              that no settlement may impose obligations on the indemnified party
              without its consent.
            </p>
            <h2>19. Termination</h2>
            <p>
              Either party may give written notice of non-renewal at least
              thirty (30) days before the end of the then-current twelve (12)
              month term.
            </p>
            <p>
              The Customer may terminate the Agreement before the end of the
              current term only by paying the remaining contract value for that
              term.
            </p>
            <p>
              Upon early termination by the Customer, all unpaid Fees for the
              remainder of the then-current term become immediately due and
              payable.
            </p>
            <p>
              If Marvel &amp; Snap terminates the Agreement before the end of
              the current term due to the Customer’s non-payment, insolvency,
              material breach, unlawful use of the Services, failure to
              cooperate, conduct creating legal, security, reputational, or
              operational risk, or any other Customer default, all unpaid Fees
              for the remainder of the then-current term will also become
              immediately due and payable.
            </p>
            <p>
              Marvel &amp; Snap may terminate the Agreement or suspend Services
              immediately upon written notice if:
            </p>
            <ul>
              <li>the Customer fails to pay overdue amounts after notice;</li>
              <li>
                the Customer materially breaches the Agreement and does not cure
                the breach within fifteen (15) days after written notice;
              </li>
              <li>
                the Customer becomes insolvent, bankrupt, dissolved, or unable
                to pay its debts;
              </li>
              <li>
                the Customer uses the Services unlawfully or in a way that
                creates legal, security, reputational, or operational risk;
              </li>
              <li>
                continuing to provide the Services would violate law,
                third-party terms, or professional obligations; or
              </li>
              <li>
                the relationship has become impractical, unsafe, or commercially
                unreasonable due to the Customer’s conduct, instructions, or
                failure to cooperate.
              </li>
            </ul>
            <p>Marvel & Snap may terminate the Agreement before the end of the
            current term if it permanently discontinues a service, technology
            platform, or business division that is material to the Services
							provided to the Customer.</p>
						<p>In such circumstances, Marvel & Snap will
            provide at least thirty (30) days' written notice. The Customer will
            not be responsible for Fees attributable to periods following the
            effective termination date. Marvel & Snap will provide reasonable
							transition assistance in accordance with Section 20.</p>
						<p>Termination
            does not relieve the Customer of payment obligations incurred before
            termination. Except where Marvel & Snap terminates the Agreement due
            to the discontinuation of Services as described above, the Customer
            remains responsible for amounts payable for the remainder of the
            current term.</p>
            <h2>20. Effect of Termination</h2>
            <p>
              Upon termination or expiry of the Agreement, Marvel &amp; Snap may
              stop providing the Services.
            </p>
            <p>
              After all outstanding invoices and amounts due have been paid,
              Marvel &amp; Snap will provide reasonable assistance with
              transition of Customer-owned materials, subject to availability,
              technical feasibility, and any applicable additional fees.
            </p>
            <p>
              Marvel &amp; Snap may retain copies of records, communications,
              files, and materials as reasonably required for legal,
              administrative, backup, compliance, accounting, or business
              continuity purposes.
            </p>
            <p>
              Marvel &amp; Snap is not required to provide transition
              assistance, transfer files, release Deliverables, or continue
              Services while amounts remain unpaid.
            </p>
            <h2>21. Publicity</h2>
            <p>
              Marvel &amp; Snap may identify the Customer as a client and may
              display the Customer’s name, logo, public website, and publicly
              available project work in its portfolio, website, proposals,
              social media, case studies, and marketing materials.
            </p>
            <p>
              The Customer may request in writing that Marvel &amp; Snap stop
              using the Customer’s name or logo in future marketing materials.
            </p>
            <p>
              Marvel &amp; Snap will make reasonable efforts to comply with such
              requests on a go-forward basis.
            </p>
            <h2>22. Electronic Communications and Signatures</h2>
            <p>
              The parties agree that communications, notices, approvals, orders,
              amendments, renewals, and signatures may be provided
              electronically unless a non-electronic method is expressly
              required by law.
            </p>
            <p>
              Electronic signatures, email approvals, online approvals, and
              other electronic records will have the same effect as written
              signatures and paper records to the extent permitted by applicable
              law.
            </p>
            <p>
              The Customer is responsible for ensuring that the email address
              and contact information provided to Marvel &amp; Snap are accurate
              and monitored.
            </p>
            <h2>23. Notices</h2>
            <p>
              Notices under the Agreement must be provided in writing and
              delivered by email, courier, registered mail, or another method
              agreed to by the parties.
            </p>
            <p>Notices to Marvel &amp; Snap must be sent to:</p>
            <p>
              Marvel &amp; Snap Inc.
              <br />
              1000 Innovation Drive, Suite 500
              <br />
              Kanata, Ontario K2K 3E7
              <br />
              Email: info@marvelandsnap.com
            </p>
            <p>
              Notices to the Customer will be sent to the contact information
              listed in the applicable order or otherwise provided by the
              Customer.
            </p>
            <p>
              Email notices are deemed received on the next Business Day after
              sending, unless the sender receives an automated delivery failure
              notice.
            </p>
            <h2>24. Force Majeure</h2>
            <p>
              Neither party will be liable for delay or failure to perform
              caused by events beyond its reasonable control, including acts of
              God, natural disasters, labour disputes, internet or hosting
              failures, cyberattacks, war, terrorism, civil unrest, supply chain
              issues, government action, power outages, third-party platform
              failures, or other events that could not reasonably have been
              prevented.
            </p>
            <p>
              The affected party must make commercially reasonable efforts to
              minimize the impact of the event and resume performance as soon as
              reasonably practicable.
            </p>
            <p>
              Force majeure does not excuse the Customer’s payment obligations.
            </p>
            <h2>25. Assignment</h2>
            <p>
              The Customer may not assign or transfer the Agreement without
              Marvel &amp; Snap’s prior written consent, not to be unreasonably
              withheld.
            </p>
            <p>
              Marvel &amp; Snap may assign the Agreement to an affiliate,
              successor, purchaser, or acquirer in connection with a merger,
              acquisition, corporate reorganization, sale of assets, or sale of
              business, provided the assignee assumes Marvel &amp; Snap’s
              obligations under the Agreement.
            </p>
            <h2>26. Independent Contractors</h2>
            <p>The parties are independent contractors.</p>
            <p>
              Nothing in the Agreement creates a partnership, joint venture,
              employment relationship, agency relationship, fiduciary
              relationship, or exclusive relationship between the parties.
            </p>
            <p>
              Neither party has authority to bind the other without prior
              written authorization.
            </p>
            <h2>27. Governing Law and Disputes</h2>
            <p>
              The Agreement is governed by the laws of the Province of Ontario
              and the federal laws of Canada applicable in Ontario.
            </p>
            <p>
              The parties agree that the courts located in Ontario will have
              exclusive jurisdiction over any dispute arising out of or relating
              to the Agreement, subject to either party’s right to seek urgent
              injunctive or equitable relief in any court of competent
              jurisdiction.
            </p>
            <p>
              Before commencing formal legal proceedings, the parties agree to
              make reasonable good faith efforts to resolve disputes through
              direct business discussions.
            </p>
            <h2>28. Survival</h2>
            <p>
              Any provision that by its nature should survive termination or
              expiry will survive, including provisions relating to payment
              obligations, ownership, intellectual property, confidentiality,
              AI-assisted services, warranty disclaimers, limitation of
              liability, indemnity, dispute resolution, governing law, records
              retention, and any accrued rights or obligations.
            </p>
            <h2>29. Updates to These Terms</h2>
            <p>Marvel &amp; Snap may update these Terms from time to time.</p>
            <p>
              Updated Terms will apply to new subscriptions immediately and to
              existing annual subscriptions upon renewal, unless otherwise
              agreed in writing or required by law.
            </p>
            <p>
              Material changes may be communicated by email, through Marvel
              &amp; Snap’s website, or through another reasonable method.
            </p>
            <p>
              Continued use of the Services after renewal or acceptance of an
              updated order constitutes acceptance of the updated Terms.
            </p>
            <h2>30. Entire Agreement</h2>
            <p>
              The Agreement constitutes the entire agreement between the parties
              regarding the Services and supersedes all prior discussions,
              proposals, understandings, emails, representations, and agreements
              relating to the same subject matter.
            </p>
            <p>
              If there is a conflict between these Terms and an applicable
              order, the applicable order will control only to the extent of the
              conflict.
            </p>
            <p>
              Any amendment to the Agreement must be in writing and accepted by
              both parties.
            </p>
            <h2>31. Severability and Waiver</h2>
            <p>
              If any provision of the Agreement is found to be invalid, illegal,
              or unenforceable, the remaining provisions will remain in full
              force and effect.
            </p>
            <p>
              A party’s failure or delay in enforcing any right or provision
              under the Agreement does not waive that right or provision.
            </p>
            <p>
              A waiver must be in writing and applies only to the specific
              instance for which it is given.
            </p>
            <h2>32. Counterparts</h2>
            <p>
              The Agreement may be accepted in counterparts and by electronic
              signature.
            </p>
            <p>
              Each counterpart will be deemed an original, and all counterparts
              together will form one agreement.
            </p>
          </Container>
        </VerticalPadding>
      </LightMode>
    </Layout>
  );
};

export default TermsOfServiceAnnual;
